import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { mensagemDeErroDeSenha } from "../lib/erroDeSenha";
import { validarSenhaForte } from "../lib/senha";
import { KeyRound, Loader2, CheckCircle2, MailWarning } from "lucide-react";

/**
 * Intercepta o fluxo de "definir senha" via magic-link (convite/recovery do
 * Supabase). Quando a pessoa clica no link do e-mail, o Supabase redireciona
 * pra cá com um token de recovery; mostramos a tela pra ela criar a senha,
 * em vez de cair no painel. Funciona em qualquer rota (envolve o app inteiro).
 *
 * ── E QUANDO O LINK JÁ MORREU ────────────────────────────────────────────────
 * O magic link é de uso único e expira por `MAILER_OTP_EXP` — config do GoTrue
 * que NÃO é ajustável pela API de `generateLink` (medido em 21/08/2026; ver
 * `api/lib/conviteAcesso.ts`). Isso é tolerável no WhatsApp, onde a pessoa vê na
 * hora, e é o caso comum no E-MAIL: chega às 18h, ela abre às 23h.
 *
 * Nesse caminho o Supabase manda de volta pro app com o erro no HASH
 * (`#error=access_denied&error_code=otp_expired&...`) e NENHUMA sessão. Sem esta
 * tela, o app tratava isso como "não logado": a pessoa caía no /login sem
 * entender por quê, no primeiro contato dela com o sistema — e o convite parecia
 * um sistema quebrado. Agora ela lê o que aconteceu e o que fazer.
 */
export function RecoveryGate({ children }: { children: React.ReactNode }) {
  const [recovery, setRecovery] = useState(false);
  const [linkMorto, setLinkMorto] = useState<string | null>(null);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
    });
    // Fallback: o evento pode ter disparado antes do listener montar.
    try {
      if (window.location.hash.includes("type=recovery")) setRecovery(true);
      const erro = erroDoLink(window.location.hash);
      if (erro) setLinkMorto(erro);
    } catch {
      /* ignore */
    }
    return () => data.subscription.unsubscribe();
  }, []);

  // O erro vem ANTES do recovery: um hash de erro nunca traz token, então não há
  // senha pra definir — mostrar o formulário aqui seria oferecer uma ação que
  // vai falhar.
  if (linkMorto) return <LinkExpiradoScreen motivo={linkMorto} />;
  if (recovery) return <SetPasswordScreen />;
  return <>{children}</>;
}

/**
 * Lê o hash que o Supabase devolve quando o link não vale mais. PURA de
 * propósito (exportada): é a única regra aqui que dá pra errar em silêncio, e
 * testá-la não pode exigir montar o app inteiro.
 *
 * Só reconhece o erro de LINK — `otp_expired` (venceu) e `access_denied` (já
 * usado, ou token trocado por um convite mais novo). Outros erros do Supabase
 * seguem o caminho normal em vez de virar a tela errada.
 */
export function erroDoLink(hash: string): string | null {
  const h = String(hash ?? "").replace(/^#/, "");
  if (!h) return null;
  const p = new URLSearchParams(h);
  const codigo = p.get("error_code") ?? "";
  const erro = p.get("error") ?? "";
  if (codigo === "otp_expired") return "expirado";
  if (erro === "access_denied") return "usado";
  return null;
}

function LinkExpiradoScreen({ motivo }: { motivo: string }) {
  const expirou = motivo === "expirado";
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm bg-card border rounded-2xl shadow-lg p-7">
        <div className="flex items-center gap-2 mb-1">
          <MailWarning className="h-5 w-5 text-amber-600" />
          <h1 className="text-lg font-bold">
            {expirou ? "Este convite expirou" : "Este link já foi usado"}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {expirou
            ? "O link de acesso vale por tempo limitado e o prazo dele passou. Não é problema no seu cadastro — é só pedir um novo."
            : "O link de acesso serve uma vez só. Se você já entrou por ele, use seu e-mail e senha. Se não, peça um novo convite."}
        </p>
        <p className="text-sm text-muted-foreground mb-5">
          Peça um novo convite a quem te cadastrou: em <b>Usuários</b>, o botão{" "}
          <b>Reenviar convite</b> manda outro link na hora.
        </p>
        <a
          href="/login"
          className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md h-10 text-sm font-semibold hover:opacity-90"
        >
          <KeyRound className="h-4 w-4" />
          Ir para o login
        </a>
      </div>
    </div>
  );
}

function SetPasswordScreen() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [ok, setOk] = useState(false);

  async function salvar() {
    setErro("");
    // Mesma regra de todas as telas de senha. Antes só conferia o tamanho, e o
    // Supabase aceita a partir de 6 — "abcdefgh", sem número, passava por aqui.
    const fraca = validarSenhaForte(pw);
    if (fraca) return setErro(fraca);
    if (pw !== pw2) return setErro("As senhas não conferem.");
    setSalvando(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSalvando(false);
    if (error) return setErro(mensagemDeErroDeSenha(error));
    setOk(true);
    setTimeout(() => {
      window.location.href = "/";
    }, 1400);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm bg-card border rounded-2xl shadow-lg p-7">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">Defina sua senha</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Crie uma senha para acessar o seu portal. Da próxima vez é só entrar com o seu e-mail e essa senha.
        </p>

        {ok ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium py-6">
            <CheckCircle2 className="h-5 w-5" /> Senha definida! Entrando…
          </div>
        ) : (
          <>
            <label className="text-xs text-muted-foreground">Nova senha</label>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full border rounded-md h-10 px-3 text-sm bg-background mb-3 mt-1"
              placeholder="8+ caracteres, com letra e número"
              autoFocus
            />
            <label className="text-xs text-muted-foreground">Confirme a senha</label>
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !salvando && salvar()}
              className="w-full border rounded-md h-10 px-3 text-sm bg-background mb-4 mt-1"
              placeholder="repita a senha"
            />
            {erro && <p className="text-xs text-rose-600 mb-3">{erro}</p>}
            <button
              onClick={salvar}
              disabled={salvando}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md h-10 text-sm font-semibold hover:opacity-90 disabled:opacity-60"
            >
              {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Definir senha e entrar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
